/**
 * AI 중복검출 자동 그룹핑 — 지도 좌표(위경도) 기반 거리 클러스터링.
 *
 * 왜 거리 기반인가: 드론 촬영이 겹치는 구간에서는 같은 물리적 결함이
 * 이미지마다 한 번씩 검출되어 지도상 거의 같은 위치에 마커가 여러 개 생긴다.
 * WFS AI 검출 피처에는 모듈 배정(solarModuleSeq)이 없으므로,
 * 지도 좌표 근접도가 "같은 결함" 판단에 쓸 수 있는 유일한 신호다.
 */

/** 위도 1도 ≈ 110.54km — 사이트 규모(수백 m)에서는 상수 근사로 충분 */
const METERS_PER_DEG_LAT = 110540;
/** 적도에서 경도 1도 ≈ 111.32km — 실제로는 cos(위도) 배율 적용 */
const METERS_PER_DEG_LNG_EQUATOR = 111320;

/**
 * 위경도 검출 목록 → 사이트 중심 기준 로컬 미터 좌표.
 * @param {Array<{ lng: number, lat: number }>} detections
 * @returns {Array<{ x: number, y: number }>}
 */
export function toLocalMeters(detections) {
  if (!Array.isArray(detections) || detections.length === 0) return [];
  const latRef =
    detections.reduce((sum, d) => sum + Number(d.lat), 0) / detections.length;
  const mPerLngDeg =
    METERS_PER_DEG_LNG_EQUATOR * Math.cos((latRef * Math.PI) / 180);
  return detections.map(d => ({
    x: Number(d.lng) * mPerLngDeg,
    y: Number(d.lat) * METERS_PER_DEG_LAT
  }));
}

/**
 * 거리 임계값 이하로 이어진 검출들을 하나의 클러스터로 묶는다.
 * 그리드 셀(임계값 크기) + union-find — 셀 인접 9칸만 비교해 O(N)에 가깝다.
 * @param {Array<{ seq: number, lng: number, lat: number }>} detections
 * @param {number} thresholdMeters 같은 결함으로 볼 최대 거리(m)
 * @returns {Array<Array<object>>} 클러스터 목록 (원본 detection 배열)
 */
export function clusterAiDetections(detections, thresholdMeters) {
  const valid = (Array.isArray(detections) ? detections : []).filter(
    d => Number.isFinite(Number(d?.lng)) && Number.isFinite(Number(d?.lat))
  );
  const n = valid.length;
  if (n === 0) return [];
  const threshold = Math.max(Number(thresholdMeters) || 0, 0.01);

  const pts = toLocalMeters(valid);

  const parent = Array.from({ length: n }, (_, i) => i);
  const find = i => {
    let root = i;
    while (parent[root] !== root) {
      parent[root] = parent[parent[root]];
      root = parent[root];
    }
    return root;
  };
  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  const cell = threshold;
  const grid = new Map();
  const keyOf = (cx, cy) => `${cx}:${cy}`;
  const thresholdSq = threshold * threshold;

  for (let i = 0; i < n; i += 1) {
    const p = pts[i];
    const cx = Math.floor(p.x / cell);
    const cy = Math.floor(p.y / cell);
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        const bucket = grid.get(keyOf(cx + dx, cy + dy));
        if (!bucket) continue;
        for (const j of bucket) {
          const ddx = pts[j].x - p.x;
          const ddy = pts[j].y - p.y;
          if (ddx * ddx + ddy * ddy <= thresholdSq) union(i, j);
        }
      }
    }
    const key = keyOf(cx, cy);
    if (!grid.has(key)) grid.set(key, []);
    grid.get(key).push(i);
  }

  const clusters = new Map();
  valid.forEach((d, i) => {
    const root = find(i);
    if (!clusters.has(root)) clusters.set(root, []);
    clusters.get(root).push(d);
  });
  return Array.from(clusters.values());
}

/**
 * 그룹에서 대표(지도에 남길 검출)를 고른다.
 * 기준: 자기 열화상 이미지의 중앙에 가장 가까운 검출 — 겹침 촬영에서
 * 중앙 검출이 렌즈 왜곡·위치 오차가 가장 적어 신뢰도가 높다.
 * 중앙 거리 정보가 없으면 seq가 가장 작은 검출.
 * @param {Array<{ seq: number }>} members
 * @param {Record<number, number>} [centerDistBySeq] seq → 이미지 중앙까지 거리(px)
 * @returns {number|null}
 */
export function pickRecommendedKeeperSeq(members, centerDistBySeq = {}) {
  if (!Array.isArray(members) || members.length === 0) return null;
  let bestSeq = null;
  let bestDist = Infinity;
  for (const m of members) {
    const seq = Number(m?.seq);
    if (!Number.isFinite(seq)) continue;
    const raw = centerDistBySeq[seq];
    const dist = Number.isFinite(raw) ? raw : Infinity;
    if (
      bestSeq === null ||
      dist < bestDist ||
      (dist === bestDist && seq < bestSeq)
    ) {
      bestSeq = seq;
      bestDist = dist;
    }
  }
  return bestSeq;
}

/**
 * 그룹별 "남길 seq 목록"으로 숨김 대상 seq를 계산한다.
 * @param {Array<{ id: string, members: Array<{ seq: number }>, recommendedSeq: number|null }>} groups
 * @param {Record<string, number[]>} keepSeqsByGroupId 그룹 id → 남길 seq 배열.
 *   항목이 없으면 추천 대표 1개만 남기는 것으로 간주.
 *   빈 배열은 "그룹 전체가 결함 아님 → 전원 숨김"을 뜻한다.
 * @param {Set<string>|string[]|null} [targetGroupIds] 지정하면 해당 그룹만 계산 대상
 *   (검토한 그룹까지만 적용할 때 사용). null이면 전체 그룹.
 * @returns {number[]} 숨길 seq 목록
 */
export function computeHiddenSeqs(groups, keepSeqsByGroupId = {}, targetGroupIds = null) {
  let target = null;
  if (targetGroupIds instanceof Set) {
    target = targetGroupIds;
  } else if (Array.isArray(targetGroupIds)) {
    target = new Set(targetGroupIds);
  }
  const hidden = [];
  for (const group of Array.isArray(groups) ? groups : []) {
    if (target && !target.has(group.id)) continue;
    const raw = keepSeqsByGroupId?.[group.id];
    const keepList = Array.isArray(raw) ? raw : [group.recommendedSeq];
    const keep = new Set(keepList.map(Number));
    for (const m of group.members) {
      const seq = Number(m.seq);
      if (!keep.has(seq)) hidden.push(seq);
    }
  }
  return hidden;
}

/**
 * 검출 목록 → 검토용 중복 그룹 목록 (2건 이상 겹친 클러스터만).
 * @param {Array<{ seq: number, lng: number, lat: number }>} detections
 * @param {number} thresholdMeters
 * @param {Record<number, number>} [centerDistBySeq]
 * @returns {Array<{ id: string, members: object[], recommendedSeq: number|null }>}
 */
export function buildDedupGroups(detections, thresholdMeters, centerDistBySeq = {}) {
  const clusters = clusterAiDetections(detections, thresholdMeters);
  return clusters
    .filter(members => members.length >= 2)
    .map(members => {
      const sorted = [...members].sort((a, b) => {
        const da = centerDistBySeq[Number(a.seq)];
        const db = centerDistBySeq[Number(b.seq)];
        const va = Number.isFinite(da) ? da : Infinity;
        const vb = Number.isFinite(db) ? db : Infinity;
        if (va !== vb) return va - vb;
        return Number(a.seq) - Number(b.seq);
      });
      const seqs = sorted.map(m => Number(m.seq));
      return {
        id: `g${Math.min(...seqs)}`,
        members: sorted,
        recommendedSeq: pickRecommendedKeeperSeq(sorted, centerDistBySeq)
      };
    });
}
